/**
 * The **entity-overlay loader** — builds the **id-keyed overlay** from authored entity
 * entries, merged at load and `{VAL}`-bound. The gather shape the buses consume
 * (`HandlerSource` / `BoundModifier[]`, via `seedHandlers`/`seedModifiers`) is
 * independent of where the overlay came from: a frozen constant and a loaded,
 * parameter-bound map are interchangeable to every consumer.
 *
 * **The two-layer template model:** an authored entry is a *template* whose atoms
 * may reference a bound parameter `{VAL}` (a tag's `Reliable {VAL}`, `Accurate {VAL}`,
 * `Knockback {VAL}`; a weapon's `Limited {VAL}`). The loader binds those placeholders
 * from a per-entity {@link Bindings} map at load — exactly when a weapon's `TagRef.val`
 * is known (attach time). A bound entry is a plain {@link OverlayEntry} the interpreter
 * never sees a `{VAL}` in.
 *
 * **What it loads from:** authored entries (`seed.ts`'s `SEED_ENTRIES`, plus any
 * sidecar packs), spanning items (by compendium id), statuses (by name), and tags (by
 * tag id). The gather seam (`gatherHandlers`/`gatherModifiers`) walks a combatant's
 * frozen equipment + statuses + weapon tags against the loaded overlay. Spatial
 * knockback / dice extras remain deferred per the calibrated step set (no faked atoms).
 */

import type { AffordanceSource, AreaEffect, Combatant, EncounterState, WeaponProfile } from "@lancer-vtt/domain";
import { COMPCON_ENGAGED_STATUS, hasStatus, selectWeaponProfile, isItemDestroyed, sideOf } from "@lancer-vtt/domain";
import { combatantRange } from "./spatialAnswerer";
import { bondmatePartner, evaluatePredicate, type Predicate } from "./predicate";
import type { ActionEffect, DeployMineAtom, EffectAtom, EffectEntry, ModifierEffect, OverlayEntry, ReplacementEffect, RollReplacementEffect, AttemptReplacementEffect, SelectProfileAtom } from "./atoms";
import type { Handler, HandlerVector } from "./triggerBus";
import type { BoundModifier } from "./modifiers";
import type { TriggerFamily } from "./family";
import { compileRuling } from "./rulingCompile";
import { checkedEntry } from "./overlayCheck";

// ── the `{VAL}` template binding ───────────────────────────────────────────────────

/**
 * The placeholder a template entry carries where a bound parameter goes. A numeric
 * field that should be `{VAL}`-bound is authored as the string `"{VAL}"` (the default
 * parameter) or `"{VAL:key}"` (a named parameter, for an entry with several). The
 * loader replaces it with the bound number; an unbound placeholder defaults to 0 and
 * is dropped (a no-op contribution) rather than crashing the interpreter.
 */
export type TemplateValue = number | "{VAL}" | (string & {});

/** The bound parameters for one entity template: `{VAL}` → the default key. */
export type Bindings = {
  /** The default `{VAL}` value (a tag's single `val`). */
  VAL?: number;
  /** Named parameters for multi-parameter templates (`{VAL:range}`). */
  [key: string]: number | undefined;
}

const PLACEHOLDER = /^\{VAL(?::([A-Za-z0-9_]+))?\}$/;

/** True iff `v` is a `{VAL}` / `{VAL:key}` placeholder string (vs a literal number). */
export function isTemplatePlaceholder(v: unknown): v is string {
  return typeof v === "string" && PLACEHOLDER.test(v);
}

/** Resolve one {@link TemplateValue} against `bindings` (a literal passes through; a placeholder binds; an unbound one → 0). */
function bindValue(v: TemplateValue, bindings: Bindings): number {
  if (typeof v === "number") return v;
  const m = PLACEHOLDER.exec(v);
  if (!m) return 0; // a non-placeholder string in a numeric slot → 0 (honest no-op)
  const key = m[1] ?? "VAL";
  return bindings[key] ?? 0;
}

/**
 * Deep-bind every `{VAL}` placeholder in an authored entry against `bindings`,
 * returning a plain {@link OverlayEntry} (no placeholders remain). Walks the JSON tree;
 * any string value that is a placeholder becomes the bound number. Structurally a pure
 * substitution — it never reshapes the entry, so an entry with no placeholders returns
 * an equal copy.
 */
export function bindEntry(entry: OverlayEntry, bindings: Bindings): OverlayEntry {
  return { id: entry.id, effects: entry.effects.map((e) => bindNode(e, bindings) as EffectEntry) };
}

/** Recursively substitute placeholders in a JSON node (object/array/primitive). */
function bindNode(node: unknown, bindings: Bindings): unknown {
  if (isTemplatePlaceholder(node)) return bindValue(node, bindings);
  if (Array.isArray(node)) return node.map((n) => bindNode(n, bindings));
  if (node && typeof node === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node)) out[k] = bindNode(v, bindings);
    return out;
  }
  return node;
}

// ── the loaded overlay ─────────────────────────────────────────────────────────────

/**
 * The loaded, id-keyed overlay — the same `Record<string, OverlayEntry>` shape
 * the buses already consume, so the `resolve` call sites don't change. Entries are
 * already `{VAL}`-bound where the binding is global (a status/tag with a fixed val);
 * per-weapon tag bindings (a weapon's own `Limited {VAL}`) bind at gather time off the
 * frozen weapon's tag list (see {@link gatherHandlers}).
 */
export type LoadedOverlay = Record<string, OverlayEntry>;

/**
 * Build a {@link LoadedOverlay} from authored entries (merge at load). Entries
 * with no `{VAL}` template load as-is; entries bound globally (a status modifier with a
 * fixed contribution) load bound. A later entry with the same id overrides an earlier
 * one (the merge precedence — a community pack overlays the core, last wins). Tag
 * *templates* load with their placeholders intact; they bind per-weapon at gather time.
 */
export function loadOverlay(entries: readonly OverlayEntry[]): LoadedOverlay {
  const out: LoadedOverlay = {};
  for (const e of entries) out[e.id] = scopeGmRulings(e);
  return out;
}

/**
 * Prefix every `gm_ruling` predicate's `id` in `entry` with the entry's own id
 * (`<entryId>/<id>`), so two entries that both author `id: "higher_elevation"` record separate
 * answers. Idempotent (an already-scoped id is left alone) and copy-on-write: an entry with no
 * `gm_ruling` is returned as the same object.
 */
function scopeGmRulings(entry: OverlayEntry): OverlayEntry {
  const prefix = `${entry.id}/`;
  let changed = false;
  const walk = (v: unknown): unknown => {
    if (Array.isArray(v)) {
      const next = v.map(walk);
      return next.some((x, i) => x !== v[i]) ? next : v;
    }
    if (v === null || typeof v !== "object") return v;
    const o = v as Record<string, unknown>;
    if (o.op === "gm_ruling" && typeof o.id === "string" && !o.id.startsWith(prefix)) {
      changed = true;
      return { ...o, id: `${prefix}${o.id}` };
    }
    let out: Record<string, unknown> | undefined;
    for (const [k, child] of Object.entries(o)) {
      const next = walk(child);
      if (next !== child) (out ??= { ...o })[k] = next;
    }
    return out ?? o;
  };
  const scoped = walk(entry) as OverlayEntry;
  return changed ? scoped : entry;
}

/**
 * Merge N already-loaded overlays into one, **last argument wins** on an id clash — the
 * same precedence {@link loadOverlay}/{@link import("./sidecar").loadSidecarChain} use for stacked entries,
 * lifted to whole `LoadedOverlay` maps. The world-boot
 * seam uses this to layer a world's loaded sidecar pack(s) on top of the engine's base
 * `SEED_OVERLAY` (intrinsics ∪ authored content, sidecar wins on collision) without
 * hand-rolling a spread in `backend/`.
 */
export function mergeOverlays(...overlays: readonly LoadedOverlay[]): LoadedOverlay {
  const out: LoadedOverlay = {};
  for (const o of overlays) Object.assign(out, o);
  return out;
}

/**
 * Look up the overlay entry for a **status name** case-/whitespace-insensitively — the
 * one keyspace that's a GM-typed free string ("lock on" / "Lock On" / "LOCK ON" all hit
 * the same entry). Compendium weapon/tag ids stay exact-match — only status lookups
 * normalize. Exact match wins first (the common, canonical case); a normalized scan is
 * the fallback.
 */
function statusOverlayEntry(overlay: LoadedOverlay, name: string): OverlayEntry | undefined {
  const exact = checkedEntry(overlay[name]);
  if (exact) return exact;
  const norm = name.trim().toLowerCase();
  for (const [k, v] of Object.entries(overlay)) {
    if (k.trim().toLowerCase() === norm) return checkedEntry(v);
  }
  return undefined;
}

// ── the gather seam (off frozen combatants) ────────────────────────────────────────

/**
 * Gather the **triggered** handlers active for an attack off the loaded overlay: the
 * attacking weapon's entry (its `on_hit`/`on_crit` `force_save`), plus any *tag*
 * templates on the weapon that carry a triggered effect (tags here are usually
 * modifiers, but the gather is shaped to bind them). The weapon-borne handlers are
 * owned by the attacker (`source`). Status- and system-borne triggered handlers gather
 * the same way.
 *
 * **DO NOT give this a composite→bare fallback like {@link gatherActions} has.** A
 * system whose own RAW ability IS a tech attack (Markerlight) is reached HERE off
 * `intent.weapon` directly AND independently off `equippedSystems` by
 * `gatherSystemHandlers` — with composite addressing (`ms_markerlight:0`) that's
 * exactly once each, correctly deduped by which vector actually applies. Adding a
 * bare fallback here would make BOTH vectors resolve the SAME bare overlay entry on
 * the carrier's own tech attack, firing its on-hit clause TWICE (a measured
 * 4-heat-instead-of-2 defect) — this is a load-bearing
 * invariant, not an inconsistency to "fix" by unifying the two gathers.
 */
export type GatherHandlersArgs = {
  state: EncounterState;
  attackerId: string;
  weapon: { id: string; tags?: { id: string; val?: number }[] } | undefined;
  overlay: LoadedOverlay;
  /**
   * Gather-time filtering is by {@link TriggerFamily} (`on`); the fine-grained
   * hit/crit/miss/declared discrimination lives in `match`, evaluated only at FIRE
   * time (`triggerBus.ts`'s `fireTrigger`). A caller gathering `"attack"` for the resolved
   * fire will also pick up a declared-only or miss-only clause from the SAME weapon —
   * intentional over-gather; `fireTrigger`'s `match` step discards what doesn't apply.
   */
  family: TriggerFamily;
};

export function gatherHandlers(args: GatherHandlersArgs): Handler[] {
  const { attackerId, weapon, overlay, family } = args;
  const out: Handler[] = [];
  if (!weapon) return out;
  const entry = checkedEntry(overlay[weapon.id]);
  if (entry) {
    for (const e of entry.effects) {
      if (e.kind === "triggered" && e.on === family) out.push({ source: attackerId, effect: e, vector: "weapon" });
    }
  }
  // Tag-borne triggered templates (bound per-weapon by the tag's val).
  for (const tag of weapon.tags ?? []) {
    const tmpl = checkedEntry(overlay[tag.id]);
    if (!tmpl) continue;
    const bound = bindEntry(tmpl, { VAL: tag.val });
    for (const e of bound.effects) {
      if (e.kind === "triggered" && e.on === family) out.push({ source: attackerId, effect: e, vector: "weapon" });
    }
  }
  return out;
}

/**
 * Gather the **triggered** handlers a combatant's **frozen pilot effects** contribute —
 * the sibling of {@link gatherHandlers} for the pilot
 * layer. Walks the combatant's frozen `pilotEffectIds` (cumulative talent-rank ids + core-
 * bonus ids, snapshotted by `spawnMech`), looks each up in the loaded overlay, and emits its
 * `triggered` handlers matching `family` — owned by the carrier (`source`). This is the
 * *active* talent/core-bonus path: a talent's on-hit rider or reaction fires through the same
 * trigger bus as a weapon's tags or a status. Pilot effects carry no `{VAL}`
 * template (they're not parameterized tags), so no per-id binding is needed. A combatant with
 * no frozen pilot effects (NPCs, or a pilotless base) contributes nothing.
 */
export type GatherPilotHandlersArgs = {
  carrierId: string;
  pilotEffectIds: readonly string[] | undefined;
  overlay: LoadedOverlay;
  /** Widened alongside {@link GatherHandlersArgs.family} — same rationale. */
  family: TriggerFamily;
};

export function gatherPilotHandlers(args: GatherPilotHandlersArgs): Handler[] {
  const { carrierId, pilotEffectIds, overlay, family } = args;
  return handlersFromRefs(PILOT_EFFECT_VECTOR.walk({ combatantId: carrierId, overlay, pilotEffectIds }), family, "pilot");
}

// ── the carrier-vector registry ──────────────────────────────────────────────────

/**
 * One vector's resolved contribution for a single combatant: an overlay entry already
 * looked up (status-name-normalized, or exact by id) plus the naming/threading a
 * consumer needs to bind it — a {@link BoundModifier}'s `name`/`carrier`/`partner`,
 * plus an optional `carrierSource` (status-only). Not
 * modifier-specific: the handler gathers ({@link gatherPilotHandlers}/{@link
 * gatherSystemHandlers} via the shared {@link handlersFromRefs}; {@link
 * gatherStatusHandlers} via its own bespoke {@link statusHandlersFromRefs}) fold onto
 * these same refs.
 */
type CarrierRef = {
  /** The resolved overlay entry — never a raw id; the vector already did the lookup. */
  entry: OverlayEntry;
  /** The gathered contribution's naming key (status name, pilot-effect id, system id). */
  name: string;
  /** The combatant this contribution binds to. */
  carrier: string;
  /** The bound partner, when this vector threads one. */
  partner?: string;
  /**
   * The carrying instance's own **caster**, threaded onto {@link
   * gatherStatusHandlers}'s `Handler.carrierSource`. Only {@link STATUS_VECTOR} populates
   * this (from `StatusInstance.source`) — pilot effects and systems have no applying-actor
   * concept distinct from their carrier, so their `walk`s never set it.
   */
  carrierSource?: string;
};

/**
 * The shared input every {@link CarrierVector}'s `walk` reads from — only the fields its
 * own vector needs. The three vectors don't share a call shape today (statuses/systems
 * read a combatant's live `state`; pilot effects take an explicit id list the caller
 * already merged via {@link combatantEffectIds}, since a caller may want pilot ∪ frame ∪
 * gear or some subset) — this context is the registry's uniform surface so a composing
 * gather ({@link gatherHandlersAt}) can walk every vector the same way
 * regardless of which fields any one vector actually reads.
 */
type CarrierWalkContext = {
  /** Omit for a vector driven purely by an explicit id list (pilot effects). */
  state?: EncounterState;
  combatantId: string;
  overlay: LoadedOverlay;
  /** Pilot/frame/gear effect ids, pre-merged by the caller — the pilot-effects vector only. */
  pilotEffectIds?: readonly string[];
  /**
   * The bound partner to thread — the pilot-effects vector's own explicit param; the
   * statuses vector ignores this and threads each instance's OWN bound partner instead
   * (a status instance carries its own `partner`; a pilot effect has no instance to carry
   * one, so the caller supplies it here — see {@link gatherPilotModifiers}'s doc).
   */
  partner?: string;
};

/** One carrier vector: a way a combatant carries overlay-modeled content, and how to walk it into {@link CarrierRef}s. */
type CarrierVector = {
  /** A short, log/diagnostic-friendly id for the vector (unused by any gather today). */
  id: string;
  walk(ctx: CarrierWalkContext): CarrierRef[];
};

const STATUS_VECTOR: CarrierVector = {
  id: "statuses",
  walk({ state, combatantId, overlay }) {
    const c = state?.combatants[combatantId];
    if (!c) return [];
    const out: CarrierRef[] = [];
    for (const inst of c.statuses) {
      // A GM-minted ruling supplies its own compiled entry instead of
      // an authored overlay lookup. This is the ONE walk through which status-carried overlay
      // content reaches the engine (`gatherModifiers` is a one-liner over it; `gatherStatusHandlers`
      // and `gatherHandlersAt`'s status slice both call it; `gatherModifiersAt`, the composed walk
      // backing immunity/resistance, reaches it via `gatherModifiers`), which is why one line here makes a ruling real
      // everywhere an authored status is real.
      //
      // TWO EDGES THIS DOES NOT REACH. Both are deliberately unexpressible in `RulingSpec`, so
      // neither is a gap to "fix" — reaching them needs an engine change, not a change here:
      //
      //   - `kind: "action"` clauses never enumerate off a status. `gatherActions` is keyed by
      //     `entityId` and its only id sources are `equippedSystems` and `combatantEffectIds`.
      //     This is why "extra Quick action" goes through the `grant_action_allowance` COMMAND
      //     rather than the carrier.
      //   - `on: "status"`-family clauses never fire on removal. `gatherHandlersForLostStatus`
      //     takes a bare `statusName` AFTER the instance is gone, so there is no instance left to
      //     read a spec off. Unpatchable at that signature; `RulingSpec` therefore has no
      //     on-lost field.
      //
      // Cosmetic-only consequences, stated so nobody re-derives them: `carriesOverlayStatus`
      // reports `false` for a ruling-only status, and `lint.ts`/`coverage.ts`/`inertTriggers.ts`/
      // `mechanization.ts` all walk the authored corpus, so a compiled ruling is never linted or
      // coverage-counted. `rulingCompile.test.ts`'s `EffectEntrySchema` pin is what covers it.
      const entry = inst.ruling
        ? { id: inst.name, effects: compileRuling(inst.ruling, inst.name) }
        : statusOverlayEntry(overlay, inst.name);
      if (!entry) continue;
      out.push({
        entry,
        name: inst.name,
        carrier: combatantId,
        ...(inst.partner !== undefined ? { partner: inst.partner } : {}),
        ...(inst.source !== undefined ? { carrierSource: inst.source } : {}),
      });
    }
    return out;
  },
};

const PILOT_EFFECT_VECTOR: CarrierVector = {
  id: "pilotEffects",
  walk({ combatantId, overlay, pilotEffectIds, partner }) {
    const out: CarrierRef[] = [];
    for (const id of pilotEffectIds ?? []) {
      const entry = checkedEntry(overlay[id]);
      if (!entry) continue;
      out.push({ entry, name: id, carrier: combatantId, ...(partner !== undefined ? { partner } : {}) });
    }
    return out;
  },
};

const SYSTEM_VECTOR: CarrierVector = {
  id: "systems",
  walk({ state, combatantId, overlay }) {
    const c = state?.combatants[combatantId];
    if (!c) return [];
    const out: CarrierRef[] = [];
    for (const sys of c.base.equippedSystems ?? []) {
      // A destroyed system's granted capabilities go dark, modifiers included.
      if (isItemDestroyed(c.current.resources, sys.instanceId)) continue;
      const entry = checkedEntry(overlay[sys.id]);
      if (!entry) continue;
      out.push({ entry, name: sys.id, carrier: combatantId });
    }
    return out;
  },
};

/**
 * The registry — one entry per carrier vector a combatant's overlay-modeled
 * content walks off of. Consumed directly (by named const) inside this module's public
 * gather functions below; exported so {@link gatherHandlersAt} can document
 * its canonical composition order against it. **Deliberately excludes
 * weapon-tag gathers** ({@link gatherHandlers}'s tag half, {@link gatherWeaponModifiers})
 * — their `{VAL}` binds per-weapon at gather time off a weapon's own tag list, not a
 * combatant-level walk, so they don't fit a `CarrierWalkContext`; see {@link
 * gatherWeaponModifiers}'s doc for the boundary. Also excludes actions ({@link
 * gatherActions}) — a single-entity lookup by activated id, not a combatant-wide walk.
 */
export const CARRIER_VECTORS: readonly CarrierVector[] = [STATUS_VECTOR, PILOT_EFFECT_VECTOR, SYSTEM_VECTOR];

/**
 * Reduce a vector's resolved {@link CarrierRef}s into `BoundModifier`s — the ONE shared
 * "for each ref, for each `modifier`-kind effect, bind name/carrier/partner" walk behind all
 * three modifier gathers below.
 *
 * **`includeAllyExclusive` is the registry-level unfiltered mode.** Default `false` is the
 * public gathers' own filtering: SKIP any clause carrying `confer ===
 * "adjacent_allies_only"` — a carrier's own fold never sees its ally-exclusive boon.
 * `true` — passed ONLY by {@link gatherConferredModifiers}'s own-effects scan — keeps those
 * clauses so they can be re-emitted onto an adjacent ally; see that function's doc for the
 * one sanctioned unfiltered call site.
 */
function modifiersFromRefs(refs: readonly CarrierRef[], includeAllyExclusive = false): BoundModifier[] {
  const out: BoundModifier[] = [];
  for (const ref of refs) {
    for (const e of ref.entry.effects) {
      if (e.kind !== "modifier") continue;
      // Both ally-exclusive confer modes (`adjacent_allies_only` and `bound_partner`)
      // are hidden from the carrier's OWN ordinary gather —
      // their boon only ever reaches an ally (or the ONE bound partner), never the granting
      // carrier's own fold. The unfiltered confer scan (`includeAllyExclusive = true`) still sees them.
      if (!includeAllyExclusive && (e.confer === "adjacent_allies_only" || e.confer === "bound_partner")) continue;
      out.push({ name: ref.name, carrier: ref.carrier, effect: e, ...(ref.partner !== undefined ? { partner: ref.partner } : {}) });
    }
  }
  return out;
}

/**
 * Reduce a vector's resolved {@link CarrierRef}s into `Handler`s matching `family` — the
 * HANDLER-side sibling of {@link modifiersFromRefs}:
 * "for each ref, for each `triggered`-kind effect firing at `family`, bind source + the ref's own
 * `name` and `entityId` (+ partner if the ref carries one)." The shared base for {@link
 * gatherPilotHandlers} and {@link gatherSystemHandlers} — both emit `{ source, effect, name,
 * entityId }` (their refs still never populate `partner`/`carrierSource`: `PILOT_EFFECT_VECTOR`'s
 * `partner` only fires when a caller threads one, which neither gather's args accept, and
 * `SYSTEM_VECTOR` never threads either field).
 *
 * **`name` here is always `ref.name` verbatim — the entry's own id** (a talent/core-bonus/pilot-
 * gear/NPC-feature id for `PILOT_EFFECT_VECTOR`, a system id for `SYSTEM_VECTOR`) — **never** the
 * per-effect `e.name ?? ref.name` fallback {@link gatherStatusHandlers} computes (see its own
 * bespoke reducer's doc just below for why that fallback stays status-only). Widened
 * (`npc-features-fielded-roster` Phase 7.2) so `compileAuthoredOffers`'s npc-feature-vector gather
 * (`reactionRegistry.ts`) has a real per-feature reaction-budget key — before this, `Handler.name`
 * was `undefined` off every pilot/system handler, which is why `toHitReactionRegistry.ts`'s own
 * named-cap fallbacks (`h.effect.name ?? h.name ?? h.source`, `h.name ?? h.source`) still check
 * `h.effect.name`/fall to `h.source` first: those checks stay correct (and now resolve to the
 * real entry id one tier earlier) rather than needing a rewrite.
 *
 * **Two lanes reached OPPOSITE verdicts on that widening — read this before touching `name` here.**
 * `resolution-narration` 1.2 needed the same id for a DISPLAY use and first got it by folding it
 * into `name` exactly as above; that lane's review called the resulting cap renamespacing a
 * regression and reverted it, adding {@link Handler.entityId} instead. `npc-features-fielded-roster`
 * Phase 7.2 then made the same fold deliberately, on the argument that a per-feature cap key is
 * the BETTER behavior, and shipped it to `main`. The reconcile keeps Phase 7.2's `name` (the live,
 * merged reaction-budget semantics — a display need must never be the reason a cap key moves) and
 * keeps `entityId` beside it as the display-only channel. They carry the same value off THIS
 * reducer today; they are not the same contract. `name` is a reaction-budget key whose value the
 * budget owner may change; `entityId` is pinned to "the carrying entity's id" and is what
 * `EffectContext.firing`/`DamageStep.source.name` read. Do not collapse one into the other.
 *
 * **`entityId: ref.name`** (`resolution-narration` 1.2) — the carrying entity's own id (a
 * talent/system id like `"t_crack_shot:2"`/`"ms_hunter_lock"`), for a DISPLAY-ONLY use (the
 * trigger bus's `EffectContext.firing`, {@link Handler.entityId}'s own doc). No reaction-budget
 * consumer may read it.
 *
 * **`gatherStatusHandlers` does NOT use this reducer** — it needs a per-effect
 * `name: e.name ?? ref.name` fallback (a status-only "reaction named-cap key" concept —
 * {@link Handler.name}'s own doc) and gathers across EVERY family (no filter at all, unlike this
 * reducer). So `gatherStatusHandlers` stays a thin bespoke walk directly over `STATUS_VECTOR.walk`'s
 * refs — still registry-derived, just not through this shared reducer.
 */
function handlersFromRefs(refs: readonly CarrierRef[], family: TriggerFamily, vector: HandlerVector): Handler[] {
  const out: Handler[] = [];
  for (const ref of refs) {
    for (const e of ref.entry.effects) {
      if (e.kind === "triggered" && e.on === family) {
        out.push({
          source: ref.carrier,
          effect: e,
          name: ref.name,
          entityId: ref.name,
          vector,
          ...(ref.partner !== undefined ? { partner: ref.partner } : {}),
        });
      }
    }
  }
  return out;
}

/**
 * Gather the **modifier** contributions a combatant's **frozen pilot effects** grant —
 * the sibling of {@link gatherModifiers}/{@link
 * gatherWeaponModifiers} for the pilot layer. Walks the combatant's frozen `pilotEffectIds`
 * and emits each entry's `modifier` effects bound to the carrier; the fold (`foldModifiers`)
 * evaluates each modifier's `condition` (a conditional core-bonus boon — e.g. +1 accuracy vs
 * an Impaired target). Named by the entity id so a consume-on-hit (rare for a pilot effect)
 * removes correctly. A combatant with no frozen pilot effects contributes nothing.
 *
 * **`partner` threading** (t_brawler:1's Hold and Lock). Unlike a status-borne modifier
 * ({@link gatherModifiers}), a pilot effect has no `StatusInstance` of its own to carry a
 * bound partner, so without this a `pilotEffectIds`-sourced (talent/core-bonus) modifier
 * could never populate
 * `BoundModifier.partner` — the `target_is`/`attacker_is who:"partner"` predicates would be
 * structurally unreachable from a talent entry (a `has_status(target,'Grappled')`
 * workaround is lossy in multi-grapple scenarios: it can't verify the
 * grapple's specific partner is the ATTACKER). The caller threads an explicit
 * `partner` id onto every returned modifier — mirroring {@link gatherModifiers}'s own
 * per-instance `partner` field, just sourced from wherever the caller's own domain logic
 * finds the relevant relationship (`resolve/attackModifiers.ts`'s attacker/target folds pass
 * `grapplePartner(state, carrierId)` — the CARRIER's own bound Grappled partner, if any —
 * so `target_is who:"partner"`/`attacker_is who:"partner"` resolve against "who I am
 * grappling," exactly like a status instance's own bound partner would). `undefined`
 * (the default) is the no-partner shape.
 */
export type GatherPilotModifiersArgs = {
  carrierId: string;
  pilotEffectIds: readonly string[] | undefined;
  overlay: LoadedOverlay;
  /** See the function doc's "`partner` threading" section. */
  partner?: string;
};

export function gatherPilotModifiers(args: GatherPilotModifiersArgs): BoundModifier[] {
  const { carrierId, pilotEffectIds, overlay, partner } = args;
  return modifiersFromRefs(PILOT_EFFECT_VECTOR.walk({ combatantId: carrierId, overlay, pilotEffectIds, partner }));
}

/**
 * Merge a combatant's frozen **pilot-effect** ids (talent ranks + core bonuses) with its frozen
 * **frame-effect** ids (`CombatantBase.frameEffectIds`), its frozen **pilot-gear-effect** ids
 * (`CombatantBase.pilotGearEffectIds`), and its frozen **NPC-feature-effect** ids
 * (`CombatantBase.npcFeatureEffectIds`, Trait/Reaction/Tech features — Gap A) into the ONE flat
 * id list {@link gatherPilotHandlers}/{@link gatherPilotModifiers} walk. All four gather
 * sources are source-agnostic — they just look each id up in the loaded overlay — so this is
 * the seam that makes a frame trait's, a pilot-gear item's, or an NPC feature's authored overlay
 * entry fold onto a spawned combatant exactly like a pilot talent's does, through the SAME
 * gather call, with zero duplicated walk logic. Order: pilot effects, then frame effects, then
 * pilot-gear effects, then NPC-feature effects (no consumer orders on this; a stable order
 * just keeps output reproducible for tests/replay).
 *
 * **COMP/CON drops the pilot effects** (RAW p.106, "AIs don't benefit from the pilot's talents or
 * other features"): while the combatant carries {@link COMPCON_ENGAGED_STATUS}, its talent ranks
 * and core bonuses gather no handler, modifier or granted action. Frame traits, systems and gear
 * still work. Every gather reads its ids here, so this is the one place the gate lives.
 *
 * `pilotGear: false` leaves the pilot-gear ids out: the granted-action mint (`enumerateActions`,
 * `carriedPilotFrameActionIds`) reaches gear actions through `base.affordances` instead.
 */
/** What {@link combatantEffectIds} reads off a combatant. */
export type EffectCarrier = {
  base: Pick<Combatant["base"], "pilotEffectIds" | "frameEffectIds" | "pilotGearEffectIds" | "npcFeatureEffectIds">;
  statuses: Combatant["statuses"];
};

export function combatantEffectIds(c: EffectCarrier | undefined, opts: { pilotGear?: boolean } = {}): string[] {
  if (c === undefined) return [];
  const { pilotEffectIds, frameEffectIds, pilotGearEffectIds, npcFeatureEffectIds } = c.base;
  return [
    ...(hasStatus(c.statuses, COMPCON_ENGAGED_STATUS) ? [] : (pilotEffectIds ?? [])),
    ...(frameEffectIds ?? []),
    ...(opts.pilotGear === false ? [] : (pilotGearEffectIds ?? [])),
    ...(npcFeatureEffectIds ?? []),
  ];
}

/**
 * Gather the **modifier** contributions active on a combatant off the loaded overlay:
 * for each active status whose name matches an overlay entry, that entry's `modifier`
 * effects bound to the carrier. (Weapon-tag modifiers — Knockback on the
 * *attacking* weapon — are gathered against the attack's weapon, not a status, so they
 * ride {@link gatherWeaponModifiers}.)
 *
 * **Ally-exclusive filtering:** SKIPS any clause
 * carrying {@link import("./atoms").ModifierEffect.confer}`==="adjacent_allies_only"` —
 * see {@link gatherPilotModifiers}'s doc for the shared rationale. This is
 * {@link modifiersFromRefs}'s default (`includeAllyExclusive` omitted); {@link
 * gatherConferredModifiers} passes `true` instead, to still discover those clauses for
 * re-emission — see that function's doc for the one sanctioned unfiltered call site.
 */
export function gatherModifiers(
  state: EncounterState,
  combatantId: string,
  overlay: LoadedOverlay,
): BoundModifier[] {
  // The instance's bound `partner` is
  // threaded by `STATUS_VECTOR.walk` itself (each status instance carries its own), so an
  // `attacker_is`/`target_is: "partner"` condition resolves it without any extra plumbing here.
  return modifiersFromRefs(STATUS_VECTOR.walk({ state, combatantId, overlay }));
}

/**
 * Gather the **modifier** contributions a mech's **frozen equipped systems** grant
 * (ms_stable_structure's passive save boon) — the systems-inventory
 * sibling of {@link gatherModifiers} (statuses) / {@link gatherPilotModifiers} (pilot
 * effects). Walks the combatant's frozen `base.equippedSystems` (`FrozenSystem[]`,
 * mech-only) and emits each equipped
 * system's overlay entry's `modifier` effects bound to the carrier, keyed by the
 * system's compendium id (so a same-name consume-on-hit would remove the right entry,
 * mirroring the other two gathers' naming). A **destroyed** system's overlay entry is
 * skipped (`isItemDestroyed` against the system's own `instanceId`) — a "destroy a
 * system of your choice" (Core p.80 System Trauma) already drops that system's granted
 * ACTIONS from the enumeration (`actionRegistry.ts`); a passive modifier is the same
 * capability going dark, so it drops too, for the same RAW reason. A non-mech
 * combatant (no `equippedSystems`) or a mech with no matching overlay entries
 * contributes nothing.
 *
 * **Ally-exclusive filtering:** SKIPS any clause
 * carrying {@link import("./atoms").ModifierEffect.confer}`==="adjacent_allies_only"` —
 * see {@link gatherPilotModifiers}'s doc for the shared rationale. This is
 * {@link modifiersFromRefs}'s default (`includeAllyExclusive` omitted); {@link
 * gatherConferredModifiers} passes `true` instead, to still discover those clauses for
 * re-emission — see that function's doc for the one sanctioned unfiltered call site.
 */
export function gatherSystemModifiers(
  state: EncounterState,
  combatantId: string,
  overlay: LoadedOverlay,
): BoundModifier[] {
  return modifiersFromRefs(SYSTEM_VECTOR.walk({ state, combatantId, overlay }));
}

/**
 * Compose **every registry vector's** modifier contributions for ONE carrier — status → pilot →
 * system, the SAME order {@link CARRIER_VECTORS} lists them in — the MODIFIER twin of {@link
 * gatherHandlersAt} (which composes the same three vectors' TRIGGERED handlers). Before this
 * (Gap B, `npc-features-fielded-roster` Phase 2, `decisions/0171`), a Resistance/Immunity grant
 * could only ever be status-borne: `seed.ts`'s since-retired `foldSelf` folded {@link
 * gatherModifiers}
 * (statuses only), so a System-typed NPC feature (Point-Defense Shield), a Trait-typed one riding
 * `PILOT_EFFECT_VECTOR` via Gap A's `npcFeatureEffectIds` (Stampede Defense, Siege Armor), or any
 * future pilot-gear/talent Resistance grant could never fold — `coverage.ts`'s
 * `REACHABLE_MODIFIER_FACETS` doc names this exact gap.
 *
 * **Every carrier-self Immunity/Resistance predicate in `seed.ts` reads this composed walk** —
 * {@link import("./seed").foldedResistance} and {@link import("./seed").immuneToDamage} arrived
 * first (Gap B), and `immuneToTechAction`/`statusBlockedByImmunity`/`hasBlanketImmunity` followed
 * in the `immunity-foldwide` widening, which retired `seed.ts`'s narrow status-only `foldSelf`
 * entirely. That staging was `decisions/0166`'s "narrow the gather at one site, not the joint,"
 * applied in reverse — one read-site widened at a time, each proved by a real spawn reaching it,
 * rather than all five at once. {@link gatherModifiers} (statuses only) survives as the
 * status-slice primitive this walk composes, not as a second immunity fold.
 */
export type GatherModifiersAtArgs = {
  state: EncounterState;
  carrierId: string;
  overlay: LoadedOverlay;
};

export function gatherModifiersAt(args: GatherModifiersAtArgs): BoundModifier[] {
  const { state, carrierId, overlay } = args;
  const c = state.combatants[carrierId];
  const pilotEffectIds = c
    ? combatantEffectIds(c)
    : [];
  return [
    ...modifiersFromRefs(STATUS_VECTOR.walk({ state, combatantId: carrierId, overlay })),
    ...modifiersFromRefs(PILOT_EFFECT_VECTOR.walk({ combatantId: carrierId, overlay, pilotEffectIds })),
    ...modifiersFromRefs(SYSTEM_VECTOR.walk({ state, combatantId: carrierId, overlay })),
  ];
}

/**
 * Gather the **triggered** handlers a mech's **frozen equipped systems** contribute — the
 * sibling of {@link gatherStatusHandlers} (statuses) / {@link gatherPilotHandlers} (pilot
 * effects) for the SYSTEMS-inventory vector. Without it, a mech-SYSTEM-carried `triggered`
 * clause (`ms_type_i_flight_system`'s `turn_end` `add_heat`, for instance) has NO gather path
 * outside the `force_save` carrier-self fold ({@link gatherSystemModifiers}'s one wired
 * consumer, `interpretForceSaveRoll`) — every other lifecycle/handler fire site gathers only
 * status- and pilot/frame-borne handlers, leaving a system's non-save triggered clause
 * structurally UNREACHABLE for a real spawned mech no matter how faithfully it was authored.
 *
 * Walks the combatant's frozen `base.equippedSystems` (`FrozenSystem[]`) — mirroring
 * {@link gatherSystemModifiers}'s own walk, including the same **destroyed-system skip**
 * (`isItemDestroyed` against the system's `instanceId` — System Trauma's "destroy a system of
 * your choice" silences a destroyed system's granted triggers exactly like it silences its
 * granted actions/modifiers) — and emits each equipped system's overlay entry's `triggered`
 * clauses matching `family`, owned by the carrier (`source`). **Family-PARAMETERIZED**, mirroring
 * {@link gatherPilotHandlers} (NOT `seedHandlers`/`seedPilotHandlers`'s hardcoded-on_hit/on_crit
 * shape) — a fire site for any other {@link TriggerFamily} calls this the exact same way. A
 * non-mech combatant (no `equippedSystems`) or a mech with no matching overlay entries
 * contributes nothing. Options-object shape (single positional arg) so a caller needing all
 * four of `state`/`carrierId`/`overlay`/`family` never brushes the `max-params(3)` gate.
 */
export type GatherSystemHandlersArgs = {
  state: EncounterState;
  carrierId: string;
  overlay: LoadedOverlay;
  family: TriggerFamily;
};

export function gatherSystemHandlers(args: GatherSystemHandlersArgs): Handler[] {
  const { state, carrierId, overlay, family } = args;
  return handlersFromRefs(SYSTEM_VECTOR.walk({ state, combatantId: carrierId, overlay }), family, "system");
}

/**
 * The `deploy_mine` atom that deployed `mine`, found on the entry its `deployedFrom` names: the atom
 * whose `groupId` minted the mine's id (`` `${deployer}:${groupId}` ``, or `` `…:${i}` `` for a multi-mine atom), searched through every nested
 * body. Its `on_fail`/`on_pass` are the clauses the detonation runs (`resolveMineDetonation`).
 * `undefined` for a mine with no `deployedFrom` (GM-placed, or deployed by the bare intent) or an
 * entry that has left the overlay.
 */
export function mineDeployAtom(mine: Combatant, overlay: LoadedOverlay): DeployMineAtom | undefined {
  const from = mine.deployedFrom;
  const entry = from !== undefined ? checkedEntry(overlay[from.entryId]) : undefined;
  if (from === undefined || entry === undefined) return undefined;
  const find = (node: unknown): DeployMineAtom | undefined => {
    if (Array.isArray(node)) {
      for (const n of node) {
        const hit = find(n);
        if (hit) return hit;
      }
      return undefined;
    }
    if (node === null || typeof node !== "object") return undefined;
    const atom = node as Partial<DeployMineAtom>;
    if (atom.op === "deploy_mine") {
      const base = `${from.combatantId}:${atom.groupId}`;
      if (mine.id === base || (mine.id.startsWith(`${base}:`) && /^\d+$/.test(mine.id.slice(base.length + 1)))) return atom as DeployMineAtom;
    }
    for (const v of Object.values(node)) {
      const hit = find(v);
      if (hit) return hit;
    }
    return undefined;
  };
  return find(entry.effects);
}

/**
 * The `on:"aura"` handlers a deployed drone's field fires (`decisions/0201`): the clauses of the
 * entry that deployed it (`deployedFrom.entryId`), owned by the deployer. Not a carrier walk: the
 * field belongs to the drone, and the deployer may carry several drones off one entry. A deploying
 * system that has since been destroyed (`deployedFrom.itemKey`) contributes nothing, as on the
 * system vector.
 */
export function gatherAuraHandlers(args: { state: EncounterState; drone: Combatant; overlay: LoadedOverlay }): Handler[] {
  const { state, drone, overlay } = args;
  const from = drone.deployedFrom;
  const deployer = from !== undefined ? state.combatants[from.combatantId] : undefined;
  const entry = from !== undefined ? checkedEntry(overlay[from.entryId]) : undefined;
  if (from === undefined || deployer === undefined || entry === undefined) return [];
  if (from.itemKey !== undefined && isItemDestroyed(deployer.current.resources, from.itemKey)) return [];
  return handlersFromRefs([{ entry, name: from.entryId, carrier: deployer.id }], "aura", "system");
}

/**
 * The `on:"aura"` handlers a standing area fires on a character inside it: the clauses of the entry
 * that made it (`AreaEffect.entry`), owned by its source. Empty for an area no entry made, or one
 * whose source is gone.
 */
export function gatherAreaHandlers(args: { state: EncounterState; area: AreaEffect; overlay: LoadedOverlay }): Handler[] {
  const { state, area, overlay } = args;
  const entry = area.entry !== undefined ? checkedEntry(overlay[area.entry]) : undefined;
  if (entry === undefined || state.combatants[area.source] === undefined) return [];
  return handlersFromRefs([{ entry, name: area.entry!, carrier: area.source }], "aura", "system");
}

/**
 * One option an attacker contributes to INVADE's menu: an {@link InvadeOptionEffect} bound to the
 * entity that carries it. `id` is `<entityId>#<key>`, the answer `invade_option` takes.
 */
export type GatheredInvadeOption = {
  id: string;
  label: string;
  when?: Predicate;
  effect: EffectAtom[];
};

/**
 * Every **invade option** `attackerId` carries, in carrier order: its pilot effects
 * ({@link combatantEffectIds}), then its live systems. The one source for both INVADE's
 * post-hit menu (`interpretChoose`'s `extend`) and the browser's "Invade ›" rows, and the same
 * two vectors its on-hit attacker clauses gather from. `when` is not applied here; see
 * {@link offeredInvadeOptions}.
 */
export function gatherInvadeOptions(state: EncounterState, attackerId: string, overlay: LoadedOverlay): GatheredInvadeOption[] {
  const c = state.combatants[attackerId];
  if (!c) return [];
  const refs = [
    ...PILOT_EFFECT_VECTOR.walk({ combatantId: attackerId, overlay, pilotEffectIds: combatantEffectIds(c) }),
    ...SYSTEM_VECTOR.walk({ state, combatantId: attackerId, overlay }),
  ];
  const out: GatheredInvadeOption[] = [];
  for (const ref of refs) {
    for (const e of ref.entry.effects) {
      if (e.kind !== "invade_option") continue;
      out.push({ id: `${ref.name}#${e.key}`, label: e.label, ...(e.when !== undefined ? { when: e.when } : {}), effect: e.effect });
    }
  }
  return out;
}

/** The invade options `attackerId` may take against `targetId`: {@link gatherInvadeOptions} minus each whose `when` fails with `self` = the attacker. */
export function offeredInvadeOptions(args: { state: EncounterState; attackerId: string; targetId: string; overlay: LoadedOverlay }): GatheredInvadeOption[] {
  const { state, attackerId, targetId, overlay } = args;
  const actors = { self: attackerId, attacker: attackerId, target: targetId };
  return gatherInvadeOptions(state, attackerId, overlay).filter((o) => o.when === undefined || evaluatePredicate(o.when, { state, actors }));
}

/**
 * Gather **conferred** modifiers (t_combined_arms:1's Shield of Blades ally
 * half, t_spotter:1's Particularize) onto `subjectId`: every OTHER combatant on
 * `subjectId`'s own side, footprint-adjacent to it (the SAME `combatantRange`
 * footprint-to-footprint metric {@link import("./predicate").ExistsAllyPredicate}
 * already reads real geometry through), whose OWN status/pilot-effect/system
 * modifiers carry a defined {@link import("./atoms").ModifierEffect.confer} (either
 * `"adjacent_allies"` or `"adjacent_allies_only"`) — those, and ONLY those, are
 * re-emitted as {@link BoundModifier}s **bound to
 * `subjectId`'s OWN fold** (not the granting carrier's), with `partner` set to the
 * GRANTING carrier's id so the confer'd clause's `condition` can read `who:"partner"`
 * to mean "the ally who granted this" (`modifiers.ts`'s `foldModifiers` already merges
 * `BoundModifier.partner` into the per-modifier `PredicateContext` — the same seam
 * Projected Shield's protector bond uses, no new context field).
 *
 * The caller decides which fold to add this to (`resolve/attackModifiers.ts`'s `atkMods`/`tgtMods`):
 * a cover-shaped confer (Combined Arms) naturally lands in the TARGET fold (the
 * carrier's `targetAccuracy` boon reaching an adjacent ally-under-attack); an
 * advantage-shaped confer (Spotter) naturally lands in the ATTACKER fold (the boon
 * reaching an adjacent ally who is THEMSELVES attacking) — `gatherConferredModifiers`
 * itself is symmetric over both, it just answers "what does `subjectId`'s
 * side/adjacency neighborhood confer onto it," the same call either way.
 *
 * `subjectId` never confers onto itself (its own modifiers already fold through the
 * ordinary {@link gatherModifiers}/{@link gatherPilotModifiers}/{@link
 * gatherSystemModifiers} gathers — this is strictly the THIRD-PARTY contribution).
 *
 * **Ally-exclusive values:** the own-effects scan
 * below walks {@link CARRIER_VECTORS} directly, feeding each vector's refs through
 * {@link modifiersFromRefs}'s `includeAllyExclusive = true` unfiltered mode, not the public
 * (self-filtering) gathers — an `"adjacent_allies_only"`-flagged clause must still be
 * visible here to be re-emitted onto the adjacent ally, even though the public gathers
 * hide it from the granting carrier's own fold. This is the one sanctioned unfiltered
 * call site. Both `confer` values (`"adjacent_allies"` and `"adjacent_allies_only"`)
 * re-emit identically here — they differ only in whether the granting carrier's OWN
 * ordinary gather also sees the clause. Walking the registry directly (rather than one
 * call per vector) means a new vector joins this scan for free, same as {@link
 * gatherHandlersAt}'s composition.
 */
export function gatherConferredModifiers(
  state: EncounterState,
  subjectId: string,
  overlay: LoadedOverlay,
): BoundModifier[] {
  return conferredOnto({ state, subjectId, overlay }, { fromRefs: (refs) => modifiersFromRefs(refs, true), conferOf: (m) => m.effect.confer });
}

/**
 * The confer scan behind {@link gatherConferredModifiers} and {@link gatherRollReplacements}:
 * every same-side, footprint-adjacent carrier's own clauses (`fromRefs`, unfiltered so the
 * ally-exclusive ones are seen) that `confer` onto `subjectId`, each bound with `partner` = the
 * granting carrier. `bound_partner` re-emits ONLY onto the carrier's designated Bondmate, never
 * every adjacent squadmate and never some OTHER `StatusInstance.partner`-bearing relationship's
 * target (a Grapple, a protector bond), so it routes through {@link bondmatePartner}, the SAME
 * reader the `adjacent_to` self clause on `t_bonded:1` uses.
 */
function conferredOnto<T>(
  where: { state: EncounterState; subjectId: string; overlay: LoadedOverlay },
  pick: { fromRefs: (refs: readonly CarrierRef[], vector: string) => T[]; conferOf: (t: T) => ModifierEffect["confer"] },
): (T & { partner: string })[] {
  const { state, subjectId, overlay } = where;
  const { fromRefs, conferOf } = pick;
  const subject = state.combatants[subjectId];
  if (!subject) return [];
  const side = sideOf(subject);
  const out: (T & { partner: string })[] = [];
  for (const id of Object.keys(state.combatants)) {
    if (id === subjectId) continue; // never confer onto yourself — that's the ordinary gather's job.
    const carrier = state.combatants[id]!;
    if (sideOf(carrier) !== side) continue; // confer is an ally-only affordance.
    if (combatantRange(state, id, subjectId) !== 1) continue; // must be footprint-adjacent.
    // No `partner` threaded onto the walk context: a confer'd clause's `partner` is the GRANTING
    // carrier, set below, whatever a vector's own `partner` would have been.
    const ctx: CarrierWalkContext = { state, combatantId: id, overlay, pilotEffectIds: combatantEffectIds(carrier) };
    for (const t of CARRIER_VECTORS.flatMap((v) => fromRefs(v.walk(ctx), v.id))) {
      const confer = conferOf(t);
      if (confer === undefined) continue;
      if (confer === "bound_partner" && bondmatePartner(state, id) !== subjectId) continue;
      out.push({ ...t, partner: id });
    }
  }
  return out;
}

/**
 * Gather the **triggered** handlers active on a combatant off the loaded overlay, at
 * ANY {@link TriggerFamily} — the sibling of {@link gatherModifiers} for the `triggered`
 * half of a status's effects
 * instead of the `modifier` half. For each active status whose name matches an overlay
 * entry, collects that entry's `triggered` clauses bound to the carrier (`source` = the
 * status's carrier — the effect fires "from" the combatant that holds the status). The
 * caller filters by trigger family (the same `fireTrigger`/bus contract every other
 * `Handler[]` gather honors) — this returns every triggered clause regardless of family,
 * mirroring `seedHandlers`'s "gather both on_hit/on_crit, let the bus filter" shape.
 *
 * The reaction-window consumer: `gatherReaction` (`reactionRegistry.ts`)
 * calls this for the target's `on_damage_taken` `optional: true` clauses, compiling them
 * into authored {@link ReactionOffer}s appended after the built-ins (Brace).
 *
 * **Also threads `Handler.carrierSource`** off each instance's own `source` — the ONE
 * gather that reaches a third-party caster. `checkpointRegistry.ts`'s
 * authored move-checkpoint offer compiler is a consumer: it filters
 * this same gather to `on_move` + `optional: true` clauses off the MOVER's own statuses and
 * routes the compiled offer to `who = carrierSource` (the mark's caster), never the mover.
 */
export function gatherStatusHandlers(
  state: EncounterState,
  combatantId: string,
  overlay: LoadedOverlay,
): Handler[] {
  // Registry-derived — walks the same
  // `STATUS_VECTOR.walk` refs `gatherModifiers` does, rather than re-scanning `c.statuses`
  // itself. `family` omitted (below) gives this function's all-families shape.
  return statusHandlersFromRefs(STATUS_VECTOR.walk({ state, combatantId, overlay }));
}

/**
 * The shared reducer behind {@link gatherStatusHandlers} AND {@link gatherHandlersAt}'s status
 * slice — same "for each ref, for each
 * `triggered`-kind effect, bind source/partner/carrierSource/name" walk, with an OPTIONAL
 * `family` filter: omitted (as {@link gatherStatusHandlers} does) it returns every
 * triggered clause regardless of family (mirroring `seedHandlers`'s own "gather all, let the
 * bus filter" shape); supplied (as {@link gatherHandlersAt} does) it narrows to one family,
 * matching what every OTHER call site already does by chaining `.filter((h) => h.effect.on ===
 * family)` onto {@link gatherStatusHandlers}'s output — so passing `family` here is provably
 * the same result, just done once instead of via a post-hoc filter.
 *
 * Deliberately NOT the same reducer as {@link handlersFromRefs} (the pilot/system-shared one)
 * — see that function's own doc for why the per-effect `name` fallback (and `carrierSource`)
 * stay status-specific rather than folding into the generic reducer.
 */
function statusHandlersFromRefs(refs: readonly CarrierRef[], family?: TriggerFamily): Handler[] {
  const out: Handler[] = [];
  for (const ref of refs) {
    for (const e of ref.entry.effects) {
      if (e.kind === "triggered" && (family === undefined || e.on === family)) {
        out.push({
          source: ref.carrier,
          effect: e,
          vector: "status",
          ...(ref.partner !== undefined ? { partner: ref.partner } : {}),
          // The reaction's own named-cap key, falling back to
          // the carrying status's own name when the clause doesn't name itself.
          name: e.name ?? ref.name,
          // Thread the carrying instance's own `source`
          // (the applying combatant) so a caller gathering a triggered clause off THIS instance
          // can reach the caster (`carrierSource`, `Handler`'s own doc) without a fourth actor
          // slot. Omitted when the instance is GM-applied (no `source` stamped).
          ...(ref.carrierSource !== undefined ? { carrierSource: ref.carrierSource } : {}),
        });
      }
    }
  }
  return out;
}

/**
 * Compose **every registry vector's** triggered handlers for ONE carrier at ONE family —
 * status → pilot → system, the SAME order
 * {@link CARRIER_VECTORS} lists them in. `resolveLifecycleTrigger` and
 * `resolveActionTakenTrigger` (`resolve.ts`), plus `interpret.ts`'s `fireForcedSaveDeclared`,
 * call this instead of hand-listing the three gathers themselves.
 *
 * **Not every fire site can use this.** A site whose own per-carrier gather order differs
 * from canonical (`fireAttackDeclared`'s attacker-side weapon→pilot→status→system, `on_miss`'s
 * attacker/target interleave) would observably reorder its emitted steps if re-pointed here —
 * those stay hand-composed, with a comment at the call site explaining why (see `resolve/attack.ts`'s `fireAttackDeclared`,
 * `resolve/attackTriggerFires.ts`'s `fireMissTriggers`).
 * Likewise a site needing a NON-canonical subset or an extra filter beyond trigger family
 * (`resolveMoveTrigger`'s status-only `optional !== true` exclusion; the on_hit/on_crit spatial
 * pre-answer and real-fire blocks, which use the `seedHandlers`/`seedPilotHandlers` API instead
 * of the registry; `reactionRegistry.ts`/`checkpointRegistry.ts`'s deliberately status-only
 * reaction-offer compilers) stays on its own bespoke gather — see each site's own comment.
 * Weapon-tag gathers ({@link gatherHandlers}) and actions ({@link gatherActions}) are the
 * documented registry outsiders (see {@link CARRIER_VECTORS}) and are never part of this
 * composition.
 */
export type GatherHandlersAtArgs = {
  state: EncounterState;
  carrierId: string;
  overlay: LoadedOverlay;
  family: TriggerFamily;
};

export function gatherHandlersAt(args: GatherHandlersAtArgs): Handler[] {
  const { state, carrierId, overlay, family } = args;
  const c = state.combatants[carrierId];
  const pilotEffectIds = c
    ? combatantEffectIds(c)
    : [];
  return [
    ...statusHandlersFromRefs(STATUS_VECTOR.walk({ state, combatantId: carrierId, overlay }), family),
    ...handlersFromRefs(PILOT_EFFECT_VECTOR.walk({ combatantId: carrierId, overlay, pilotEffectIds }), family, "pilot"),
    ...handlersFromRefs(SYSTEM_VECTOR.walk({ state, combatantId: carrierId, overlay }), family, "system"),
  ];
}

/**
 * Gather the **triggered** handlers a NAMED status's overlay entry declares, by direct
 * id lookup rather than scanning a combatant's *current* statuses: `on_status_lost` fires
 * against **post-removal** state, so by the time it's gathered
 * the lost status is already gone from `carrierId`'s `statuses` array and a
 * {@link gatherStatusHandlers}-style scan would find nothing. The caller (the room's
 * duration-bus expiry path) supplies the status name from the just-computed
 * `status_remove` steps (captured BEFORE they commit) and calls this AFTER they commit,
 * so the fired handler's `on_status_lost` clause sees the world with the status already
 * gone (e.g. Stay of Execution's own damage/status immunity is gone by the time its
 * `on_status_lost` → Stunned handler fires).
 */
export function gatherHandlersForLostStatus(
  carrierId: string,
  statusName: string,
  overlay: LoadedOverlay,
): Handler[] {
  const entry = statusOverlayEntry(overlay, statusName);
  if (!entry) return [];
  const out: Handler[] = [];
  for (const e of entry.effects) {
    if (e.kind === "triggered") out.push({ source: carrierId, effect: e, vector: "status" });
  }
  return out;
}

/**
 * Gather the **attacker-side modifier** contributions a weapon's *tags* grant (the
 * `{VAL}`-bound tag templates — Accurate {VAL}, etc.), bound per-weapon. These fold
 * into the attacker side of the to-hit math exactly like a status modifier; they're
 * named by the tag id (so a consume-on-hit tag, if any, removes correctly). A weapon
 * with no overlay-modeled tags contributes nothing.
 *
 * **Deliberately outside the carrier-vector registry**: every registry vector walks a
 * *combatant's* frozen
 * state (`state.combatants[id]`) and looks up an *already-authored* overlay entry by a
 * fixed id. A weapon tag is neither — the candidate list comes from the attack's own
 * `weapon.tags` (not a combatant-level field), and its `{VAL}` must bind PER-WEAPON at
 * gather time (`bindEntry(tmpl, { VAL: tag.val })`) before the entry even has effects to
 * walk, so there's no plain `OverlayEntry` to hand a `CarrierRef` until after that bind.
 * Forcing it into `CarrierWalkContext` would mean smuggling a weapon through a context
 * shaped for combatant ids — the unify-can-overgrant lesson applies to mechanisms, not
 * just entities. Left as its own function.
 */
export function gatherWeaponModifiers(
  attackerId: string,
  weapon: { tags?: { id: string; val?: number }[] } | undefined,
  overlay: LoadedOverlay,
): BoundModifier[] {
  const out: BoundModifier[] = [];
  for (const tag of weapon?.tags ?? []) {
    const tmpl = checkedEntry(overlay[tag.id]);
    if (!tmpl) continue;
    const bound = bindEntry(tmpl, { VAL: tag.val });
    for (const e of bound.effects) {
      if (e.kind === "modifier") out.push({ name: tag.id, carrier: attackerId, effect: e });
    }
  }
  return out;
}

/** Reduce a vector's {@link CarrierRef}s into the replacements on `on`, hiding the ally-exclusive ones unless `includeAllyExclusive` (the confer scan). */
function replacementsFromRefs<C extends RollReplacementEffect | AttemptReplacementEffect>(refs: readonly CarrierRef[], vector: string, opts: { on: C["on"]; includeAllyExclusive?: boolean }): (BoundReplacement<C> & { partner?: string })[] {
  const out: (BoundReplacement<C> & { partner?: string })[] = [];
  for (const ref of refs) {
    for (const e of ref.entry.effects) {
      if (e.kind !== "replacement" || e.on !== opts.on) continue;
      const clause = e as C;
      if (opts.includeAllyExclusive !== true && (clause.confer === "adjacent_allies_only" || clause.confer === "bound_partner")) continue;
      out.push({ carrier: ref.carrier, entityId: ref.name, clause, vector, ...(ref.partner !== undefined ? { partner: ref.partner } : {}) });
    }
  }
  return out;
}

const rollReplacementsFromRefs = (refs: readonly CarrierRef[], vector: string, includeAllyExclusive = false): BoundRollReplacement[] =>
  replacementsFromRefs<RollReplacementEffect>(refs, vector, { on: "roll", includeAllyExclusive });

/** A gathered attempt replacement (Invisible's coin); `partner` is the carrying status's partner, or the granting ally when conferred. */
export type BoundAttemptReplacement = BoundReplacement<AttemptReplacementEffect> & { partner?: string };

/**
 * Every `on: "attack_attempt"` replacement on `targetId` (`event-engine-to-hit` 3.1): off its
 * statuses, pilot effects and systems, and an adjacent ally's conferred clause, the sources the
 * target-side modifier fold read Invisible's `missChance` from.
 */
export function gatherAttemptReplacements(state: EncounterState, targetId: string, overlay: LoadedOverlay): BoundAttemptReplacement[] {
  const pilotEffectIds = combatantEffectIds(state.combatants[targetId]);
  const own = (refs: readonly CarrierRef[], vector: string, includeAllyExclusive = false) => replacementsFromRefs<AttemptReplacementEffect>(refs, vector, { on: "attack_attempt", includeAllyExclusive });
  return [
    ...own(STATUS_VECTOR.walk({ state, combatantId: targetId, overlay }), STATUS_VECTOR.id),
    ...own(PILOT_EFFECT_VECTOR.walk({ combatantId: targetId, overlay, pilotEffectIds }), PILOT_EFFECT_VECTOR.id),
    ...own(SYSTEM_VECTOR.walk({ state, combatantId: targetId, overlay }), SYSTEM_VECTOR.id),
    ...conferredOnto({ state, subjectId: targetId, overlay }, { fromRefs: (refs, vector) => own(refs, vector, true), conferOf: (r) => r.clause.confer }),
  ];
}

/** Named args for {@link gatherRollReplacements}. */
export type GatherRollReplacementsArgs = {
  state: EncounterState;
  rollerId: string;
  /** The firing weapon, whose tags carry their own clauses. */
  weapon?: { tags?: { id: string; val?: number }[] } | undefined;
  overlay: LoadedOverlay;
  /** The roller's bound Grappled partner, threaded to its pilot-effect clauses as an attack fold threads it. */
  partner?: string;
};

/**
 * Every `on: "roll"` replacement that can reach `rollerId`'s rolls (`event-engine-to-hit` R10),
 * from the same sources the attacker-side modifier fold reads (`resolve/attackModifiers.ts`): its
 * statuses, the firing weapon's tags, its pilot effects, its systems, and an adjacent ally's
 * conferred clause. Unfiltered by match: the caller evaluates each against the roll's facts.
 */
export function gatherRollReplacements(args: GatherRollReplacementsArgs): BoundRollReplacement[] {
  const { state, rollerId, weapon, overlay, partner } = args;
  const tags: BoundRollReplacement[] = [];
  for (const tag of weapon?.tags ?? []) {
    const tmpl = checkedEntry(overlay[tag.id]);
    if (!tmpl) continue;
    for (const e of bindEntry(tmpl, { VAL: tag.val }).effects) if (e.kind === "replacement" && e.on === "roll") tags.push({ carrier: rollerId, entityId: tag.id, clause: e, vector: "weapon" });
  }
  const pilotEffectIds = combatantEffectIds(state.combatants[rollerId]);
  return [
    ...rollReplacementsFromRefs(STATUS_VECTOR.walk({ state, combatantId: rollerId, overlay }), STATUS_VECTOR.id),
    ...tags,
    ...rollReplacementsFromRefs(PILOT_EFFECT_VECTOR.walk({ combatantId: rollerId, overlay, pilotEffectIds, ...(partner !== undefined ? { partner } : {}) }), PILOT_EFFECT_VECTOR.id),
    ...rollReplacementsFromRefs(SYSTEM_VECTOR.walk({ state, combatantId: rollerId, overlay }), SYSTEM_VECTOR.id),
    ...conferredOnto({ state, subjectId: rollerId, overlay }, { fromRefs: (refs, vector) => rollReplacementsFromRefs(refs, vector, true), conferOf: (r) => r.clause.confer }),
  ];
}

/**
 * A gathered **action**: the actor-initiated effect bound to the combatant that owns it,
 * found by the activated entity `id` off the loaded overlay. The activate intent resolver
 * looks one up by the entity id the intent names; the interpreter binds its `effect`'s
 * `self` to `owner`. A tag-borne action would bind per-weapon the same way.
 */
export type GatheredAction = {
  /** The combatant activating the action (its `self`/owner when interpreted). */
  owner: string;
  /** The action entity id (for the activation-cost advisory + diagnostics). */
  id: string;
  /** The action effect (carries its `activation` cost, `condition`, `effect` atoms). */
  effect: ActionEffect;
}

/**
 * Parse a composite **gear-granted action id** `<bareId>:<index>` into its bare id;
 * `undefined` if `id` isn't that shape (colon-separated, a non-negative integer suffix).
 * Mirrors `actionRegistry.ts`'s `parseGrantedId` structurally (kept as a separate,
 * loader-local copy rather than imported — `actionRegistry.ts` already imports
 * {@link gatherActions} from here, so importing back would cycle).
 */
export function bareGrantedId(id: string): string | undefined {
  const colon = id.lastIndexOf(":");
  if (colon <= 0 || colon === id.length - 1) return undefined;
  const index = Number(id.slice(colon + 1));
  if (!Number.isInteger(index) || index < 0) return undefined;
  return id.slice(0, colon);
}

/**
 * Gather the **action** effects an entity grants its owner off the loaded overlay,
 * keyed by the entity `id` the activate intent names. Returns one
 * {@link GatheredAction} per `action`-kind effect on that entry (an item may declare
 * several actions). Tag templates that carry an action bind per-weapon (`bindings`),
 * mirroring {@link gatherHandlers}; a fixed-val action binds at load.
 *
 * ## Two ways in
 *
 * **1. `source` — the structured path** (`decisions/0176`; `affordance-table` 1.5). A caller that
 * already resolved the wire id at the door (`resolveAffordance`, `economy/carriedActions.ts`) holds
 * the frozen {@link import("@lancer-vtt/domain").Affordance} record and passes its `source`. The
 * class is then *known*, so no string is parsed here at all:
 *
 * - `granted` → the entry is `source.itemId` (the bare item id the authoring harness locks a pack
 *   entry to) and **exactly one** clause is returned, the one at `source.index`.
 * - `effect` → the entry is `source.entityId` and **every** clause is returned. That is required
 *   behaviour, not a fallback: one bare `everest_hyperspec_fuel_injector` fires both of Power Up's
 *   authored clauses off the one entry-level cap, which is why an `effect` source carries no index.
 *
 * This reaches the same clause the string path below computes — the record's `index` IS the `:N` of
 * its key ({@link import("@lancer-vtt/domain").affordanceKey}), both being a position in
 * `decisions/0123`'s unified affordance space — but without a `lastIndexOf(":")` that is only
 * correct if the caller already knew the class. `resolveActivate` is the first caller on it.
 *
 * **When the record's address misses, the string path still runs.** `resolveAffordance`'s arm 3
 * synthesizes an `effect` record for any id the carrier does not hold — including a COMPOSITE one
 * (`ms_type_i_flight_system:0` fired by a mech that never equipped it) — and that composite is not
 * an overlay key. The resolver is deliberately permissive about possession (the carrier-ownership
 * wall is a separate, room-tier check, `decisions/0143`), so an entry miss under `source` falls
 * through to the string path instead of returning `[]`: a structured gather never resolves LESS
 * than the string one would. An out-of-range `index` on an entry that WAS found is not a miss — it
 * is the same honest `[]` the string path's `:N` gives.
 *
 * **Named clauses.** `<entryId>#<name>` selects the one action clause carrying that
 * {@link ActionEffect.name} (Blademaster's Feint and Trip on `t_duelist:2`). It is tried first on
 * the string path, and only when the id is not itself an overlay key.
 *
 * **2. `entityId` alone — the string path**, unchanged, below. Every other call site is still on it
 * and structurally so: the freezer (`spawn/actionFreeze.ts`) is *minting* the records, and the
 * browser enumerator / `intentCost` / `isProtocolAction` / `directedDeployRequirement` walk a
 * carried id set rather than holding one resolved record. So {@link bareGrantedId} **stays** — it is
 * the string path's own parser, not a duplicate of the door's, and it goes when Phase 3 moves those
 * enumerations onto `base.affordances`.
 *
 * **Composite → bare fallback:** `freezePilotGearActionIds` freezes a pilot's gear
 * actions as composite ids (`pg_frag_grenades:0`), but the authoring harness locks a
 * pack entry's id to the BARE gear id (`pg_frag_grenades`).
 *
 * **Why the fallback exists.** It is NOT a structural necessity — both `freezeActionIds`
 * and `freezePilotGearActionIds` index into a
 * compendium record's OWN `actions[]` array, pack data fully determined at author time
 * and stable across every pilot who equips it (`ms_aggressive_system_sync:1` is exactly
 * as knowable as `ms_aggressive_system_sync:0`). It is an
 * **authoring-ergonomics convenience**: the harness locks a pack ENTRY id to the bare
 * form, so an author writing the overlay entry itself never has to guess or restate the
 * composite suffix. A pack author who wants to *match* a specific instance in a `match`
 * field (not the entry id) is free to write the composite value directly — only the
 * entry id is locked to bare, a match value is ordinary data (see `AttackMatch.actionId`).
 * On an exact-match miss for a composite id, fall back to the
 * bare id so an authored pack entry resolves for any frozen instance. The bare id stays
 * the pack's canonical key; only the lookup gains the fallback, never the overlay's
 * stored keys.
 *
 * **This fallback is deliberately NOT given to `gatherHandlers`** — doing so would arm a
 * handler double-gather; see that function's own doc comment.
 *
 * **Clause selection by `:N`:** when `entityId` resolves via the bare-id FALLBACK above —
 * i.e. it did NOT match an overlay entry directly, but its colon-stripped form did — the
 * trailing `:N` is a position in the item's unified affordance space (`actions[]` first,
 * then `deployables[]`) and selects ONLY the Nth `kind:"action"`
 * clause on the resolved entry, instead of returning all of them. This is what lets one
 * item (Smoke/HEX Charges) surface two rows — a thrown grenade and a deployable mine —
 * bound to two DISTINCT authored clauses rather than every row firing every clause.
 *
 * **Exact-match ids are NEVER indexed, deliberately.** An id that hits the overlay
 * directly (e.g. a pilot-effect rank id like `t_crack_shot:1`, whose `:N` is a RANK, not
 * an action-array index — see `pilotEffectDisplay`'s doc comment) returns every
 * `action`-kind clause on its entry. So does a bare id
 * with no colon, and an id whose suffix doesn't parse as a non-negative integer
 * (`bareGrantedId` returns `undefined`) — covering every seed/intrinsic action (`brace`,
 * `grapple`, talent and core-bonus entries).
 *
 * An out-of-range `N` (no clause at that position — e.g. a deployable row whose clause
 * isn't authored yet) returns `[]`, the same honest-degrade shape a missing overlay entry
 * produces.
 */
export type GatherActionsArgs = {
  ownerId: string;
  entityId: string;
  overlay: LoadedOverlay;
  bindings?: Bindings;
  /**
   * The resolved affordance's **structured source** (`decisions/0176`; `affordance-table` 1.5).
   * When present it selects the overlay entry AND the clause outright, so this gather never
   * parses `entityId` — see {@link gatherActions}' "Two ways in". `entityId` is still what the
   * returned {@link GatheredAction.id} carries, so the advisory/diagnostic text a caller emits
   * names the id the wire actually asked for, exactly as it did before.
   */
  source?: AffordanceSource;
};

export function gatherActions(args: GatherActionsArgs): GatheredAction[] {
  const { ownerId, entityId, overlay, bindings, source } = args;
  if (source !== undefined) {
    // The STRUCTURED path — the class is already known, so there is nothing to parse.
    const entry = checkedEntry(overlay[source.kind === "granted" ? source.itemId : source.entityId]);
    // NO overlay entry at the record's own address ⇒ fall THROUGH to the string path rather than
    // returning `[]`. See "when the record's address misses" in the doc above: a structured gather
    // must never resolve LESS than the string one did.
    if (entry) {
      const clauses = actionClausesOf(entry, bindings);
      if (source.kind === "effect") return clauses.map((e) => ({ owner: ownerId, id: entityId, effect: e }));
      const selected = indexedActionClauses(clauses)[source.index];
      // An out-of-range index is the same honest `[]` the string path's `:N` produces — NOT a miss
      // to fall through on; the entry was found, it simply has no clause there.
      return selected ? [{ owner: ownerId, id: entityId, effect: selected }] : [];
    }
  }
  const named = namedActionClause(entityId, overlay, bindings);
  if (named !== undefined) return named === null ? [] : [{ owner: ownerId, id: entityId, effect: named }];
  const bare = bareGrantedId(entityId);
  const exactHit = checkedEntry(overlay[entityId]);
  const entry = exactHit ?? (bare !== undefined ? checkedEntry(overlay[bare]) : undefined);
  if (!entry) return [];
  const actionClauses = actionClausesOf(entry, bindings);
  if (exactHit === undefined && bare !== undefined) {
    // Resolved via the bare-id fallback for a composite id ⇒ `:N` indexes the Nth unnamed action clause.
    const index = Number(entityId.slice(entityId.lastIndexOf(":") + 1));
    const selected = indexedActionClauses(actionClauses)[index];
    return selected ? [{ owner: ownerId, id: entityId, effect: selected }] : [];
  }
  return actionClauses.map((e) => ({ owner: ownerId, id: entityId, effect: e }));
}

/**
 * The action clause a **named-clause id** `<entryId>#<name>` addresses ({@link ActionEffect.name}):
 * `undefined` when `id` is not that shape or `<entryId>` is no overlay entry (the other paths
 * then run), `null` when the entry has no action clause of that name.
 */
function namedActionClause(id: string, overlay: LoadedOverlay, bindings: Bindings | undefined): ActionEffect | null | undefined {
  const hash = id.lastIndexOf("#");
  if (hash <= 0 || overlay[id] !== undefined) return undefined;
  const entry = checkedEntry(overlay[id.slice(0, hash)]);
  if (!entry) return undefined;
  const name = id.slice(hash + 1);
  return allActionClausesOf(entry, bindings).find((e) => e.name === name) ?? null;
}

/**
 * The ids a pilot or frame effect's action clauses are activated by: `<entryId>#<name>` for each
 * clause when they are named ({@link ActionEffect.name}), else the entry id alone, which fires
 * them all.
 */
export function actionIdsOf(entryId: string, overlay: LoadedOverlay): string[] {
  const entry = checkedEntry(overlay[entryId]);
  if (!entry) return [];
  const clauses = actionClausesOf(entry, undefined);
  return clauses.every((e) => e.name !== undefined) ? clauses.map((e) => `${entryId}#${e.name}`) : [entryId];
}

/**
 * Bind an overlay entry (when `bindings` are given) and keep its own `kind:"action"` clauses, in
 * authored order. A `usableBy` clause is left out: it is a deployed drone's action for others
 * ({@link deployedActionClausesOf}), never a row or a `:N` index of the entry's carrier.
 */
/**
 * The action clauses a composite `<itemId>:<N>` (or a granted record's index) addresses: the
 * unnamed ones, in authored order. A named clause is addressed only as `<entryId>#<name>`, so a
 * feature can add an action its compendium record lacks without landing on one of the record's own
 * indices (Suppress's Quick Action beside its Moving Target reaction row).
 */
function indexedActionClauses(clauses: Extract<EffectEntry, { kind: "action" }>[]): Extract<EffectEntry, { kind: "action" }>[] {
  return clauses.filter((e) => e.name === undefined);
}

function actionClausesOf(entry: OverlayEntry, bindings: Bindings | undefined): Extract<EffectEntry, { kind: "action" }>[] {
  return allActionClausesOf(entry, bindings).filter((e) => e.usableBy === undefined);
}

function allActionClausesOf(entry: OverlayEntry, bindings: Bindings | undefined): Extract<EffectEntry, { kind: "action" }>[] {
  const bound = bindings ? bindEntry(entry, bindings) : entry;
  return bound.effects.filter((e): e is Extract<EffectEntry, { kind: "action" }> => e.kind === "action");
}

/** The `usableBy` action clauses of `entryId` (named, per the schema), with their `<entryId>#<name>` ids: what a drone it deployed offers its allies. */
export function deployedActionClausesOf(entryId: string, overlay: LoadedOverlay): { id: string; clause: Extract<EffectEntry, { kind: "action" }> }[] {
  const entry = checkedEntry(overlay[entryId]);
  if (!entry) return [];
  return allActionClausesOf(entry, undefined).flatMap((clause) => (clause.usableBy !== undefined && clause.name !== undefined ? [{ id: `${entryId}#${clause.name}`, clause }] : []));
}

/**
 * Select a weapon's active firing profile **by combatant state** — the state-lookup half
 * of profile selection. Reads the weapon's overlay entry for a `select`
 * effect; if present, reads the named `resource` off the acting combatant (or its `status`, as 1
 * while carried and 0 otherwise) and picks the highest tier whose `at` ≤ that value, resolving that tier's `profile` via the
 * pure {@link selectWeaponProfile}. No `select` effect, or no tier matched and no
 * `fallback`, returns the weapon's default profile unchanged. Emits no step — it only
 * changes *which* frozen profile the attack resolves with. A pure function (state +
 * weapon → profile), so the resolver records the chosen profile's outcome the same way
 * a recorded `profile` choice would.
 */
export type SelectProfileByStateArgs = {
  state: EncounterState;
  actorId: string;
  weapon: WeaponProfile;
  overlay: LoadedOverlay;
};

export function selectProfileByState(args: SelectProfileByStateArgs): WeaponProfile {
  const { state, actorId, weapon, overlay } = args;
  const entry = checkedEntry(overlay[weapon.id]);
  if (!entry) return weapon;
  const sel = entry.effects.find((e): e is { kind: "select"; select: SelectProfileAtom } => e.kind === "select");
  if (!sel) return weapon;
  const { resource, status, who, tiers, fallback } = sel.select;
  const ownerId = who === undefined || who === "self" || who === "attacker" ? actorId : who === "target" ? actorId : who;
  const c = state.combatants[ownerId];
  const value = status !== undefined ? (c && hasStatus(c.statuses, status) ? 1 : 0) : (c?.current.resources?.[resource ?? ""] ?? 0);
  // Highest `at` ≤ value wins (the tiers describe ascending charge thresholds).
  const matched = [...tiers].sort((a, b) => b.at - a.at).find((t) => value >= t.at);
  const profileId = matched?.profile ?? fallback;
  return profileId ? selectWeaponProfile(weapon, profileId) : weapon;
}

/**
 * The profile state selects for `weapon`, or `undefined` when its overlay entry carries no `select`
 * clause (the profile is then the intent's free pick). The gate `checkProfileByState` and the
 * browser's `activeProfiles` projection both read it.
 */
export function stateSelectedProfile(args: SelectProfileByStateArgs): WeaponProfile | undefined {
  if (!checkedEntry(args.overlay[args.weapon.id])?.effects.some((e) => e.kind === "select")) return undefined;
  return selectProfileByState(args);
}

/** True iff `combatantId` carries any overlay-modeled status (test/advisory helper). */
export function carriesOverlayStatus(state: EncounterState, combatantId: string, overlay: LoadedOverlay): boolean {
  const c = state.combatants[combatantId];
  if (!c) return false;
  return c.statuses.some((inst) => statusOverlayEntry(overlay, inst.name) !== undefined);
}

/**
 * One gathered `replacement` clause, bound to the combatant that carries it and the entry it belongs
 * to; `vector` is the carrier vector it came off ({@link CarrierVector.id}), which the F2a order reads.
 */
export type BoundReplacement<C extends ReplacementEffect = ReplacementEffect> = { carrier: string; entityId: string; clause: C; vector: string };

/** A gathered roll replacement; `partner` is the granting ally, when the clause was conferred. */
export type BoundRollReplacement = BoundReplacement<RollReplacementEffect> & { partner?: string };

/**
 * Every `replacement` clause on the board (`decisions/0211`), walked through {@link CARRIER_VECTORS}
 * per combatant (status → pilot effect → system), combatants in map order. Cost settlement
 * (`events/gameEvents.ts`) puts them in the F2a order for each event and applies the first that
 * matches. Weapon and tag vectors carry none.
 */
export function gatherReplacements(state: EncounterState, overlay: LoadedOverlay): BoundReplacement[] {
  const out: BoundReplacement[] = [];
  for (const [id, c] of Object.entries(state.combatants)) {
    const pilotEffectIds = combatantEffectIds(c);
    for (const vector of CARRIER_VECTORS) {
      for (const ref of vector.walk({ state, combatantId: id, overlay, pilotEffectIds })) {
        for (const e of ref.entry.effects) if (e.kind === "replacement") out.push({ carrier: ref.carrier, entityId: ref.name, clause: e, vector: vector.id });
      }
    }
  }
  return out;
}
